import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Image, Link, ListChecks, Pencil, Plus, Trash2, Type } from 'lucide-react'

import { LayoutComponentDialog } from '@/components/LayoutComponentDialog'
import { SortableTableRow, SortableTableRows } from '@/components/SortableTableRows'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { mediaKindsOf, mimeTypesFor } from '@/lib/mediaTypes'
import type { LayoutComponent, LayoutComponentType } from '@/services/sites/layoutsApi'
import { formatNumber } from '@/utils/format'

const TYPE_ICONS: Record<LayoutComponentType, typeof Image> = {
  ASSET: Image,
  TEXT: Type,
  URL: Link,
  CHOICE: ListChecks,
}

const ADD_ORDER: LayoutComponentType[] = ['ASSET', 'TEXT', 'URL', 'CHOICE']

/** The tag prefix each type starts from: image, image2, image3… */
const TAG_BASES: Record<LayoutComponentType, string> = {
  ASSET: 'image',
  TEXT: 'text',
  URL: 'url',
  CHOICE: 'choice',
}

function uniqueTag(base: string, components: LayoutComponent[]): string {
  const taken = new Set(components.map((c) => c.tag))
  let n = 1
  while (taken.has(n === 1 ? base : `${base}${n}`)) n++
  return n === 1 ? base : `${base}${n}`
}

/** A new component with the same defaults the old UI gave it. */
function newComponent(type: LayoutComponentType, components: LayoutComponent[]): LayoutComponent {
  const tag = uniqueTag(TAG_BASES[type], components)
  const base = { type, tag, required: 'MANDATORY' }
  switch (type) {
    case 'ASSET':
      return { ...base, mimeTypes: mimeTypesFor('image'), maxFileSizeBytes: 200 * 1024 }
    case 'TEXT':
      return { ...base, textType: 'TEXT', minLength: 0, maxLength: 50, defaultValue: '' }
    case 'CHOICE':
      return { ...base, display: 'COMBO', options: [], defaultValue: '' }
    default:
      return base
  }
}

export interface LayoutComponentsEditorProps {
  value: LayoutComponent[]
  onChange: (value: LayoutComponent[]) => void
  disabled?: boolean
}

/**
 * The components a layout asks its creatives for — media, text, URLs and
 * choices — each referenced from the render template by its tag. Order here
 * is the order the creative form shows them in.
 */
export function LayoutComponentsEditor({ value, onChange, disabled }: LayoutComponentsEditorProps) {
  const { t } = useTranslation()
  // The index being edited, or -1 for a component that is being added.
  const [editing, setEditing] = useState<{ index: number; component: LayoutComponent } | null>(null)

  const summary = (c: LayoutComponent): string[] => {
    const parts = [t(`layouts.required.${c.required}`)]
    if (c.type === 'ASSET') {
      const kinds = mediaKindsOf(c.mimeTypes).map((k) => t(`layouts.mediaKinds.${k.id}`))
      if (kinds.length) parts.push(kinds.join(', '))
      if (c.maxFileSizeBytes) parts.push(`${formatNumber(Math.round(c.maxFileSizeBytes / 1024))} KB`)
      if (c.aspectRatio) parts.push(t(`layouts.aspectRatios.${c.aspectRatio}`))
      else if (c.customAspectRatioWidth && c.customAspectRatioHeight) {
        parts.push(`${c.customAspectRatioWidth}:${c.customAspectRatioHeight}`)
      }
    }
    if (c.type === 'TEXT') {
      parts.push(t(`layouts.textTypes.${c.textType ?? 'TEXT'}`))
      if (c.textType === 'CODE') {
        if (c.minLength) parts.push(t('layouts.componentEditor.minChars', { count: c.minLength }))
      } else {
        parts.push(t('layouts.componentEditor.charRange', { min: c.minLength ?? 0, max: c.maxLength ?? 0 }))
      }
    }
    if (c.type === 'CHOICE') {
      parts.push(t(`layouts.choiceDisplays.${c.display ?? 'COMBO'}`))
      if (c.options?.length) parts.push(c.options.join(' · '))
    }
    return parts
  }

  const ids = value.map(rowId)
  const reorder = (next: string[]) => onChange(next.map((id) => value[ids.indexOf(id)]))

  const save = (component: LayoutComponent) => {
    if (!editing) return
    onChange(
      editing.index < 0 ? [...value, component] : value.map((c, i) => (i === editing.index ? component : c))
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-(--ad-text-muted)">{t('layouts.componentsHint')}</p>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={disabled}>
              <Plus className="size-4" />
              {t('layouts.addComponent')}
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {ADD_ORDER.map((type) => {
              const Icon = TYPE_ICONS[type]
              return (
                <DropdownMenuItem key={type} onSelect={() => setEditing({ index: -1, component: newComponent(type, value) })}>
                  <Icon className="size-4" />
                  {t(`layouts.componentTypes.${type}`)}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {value.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('layouts.noComponents')}</EmptyTitle>
            <EmptyDescription>{t('layouts.noComponentsHint')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-0" />
              <TableHead>{t('common.type')}</TableHead>
              <TableHead>{t('layouts.componentEditor.tag')}</TableHead>
              <TableHead>{t('layouts.componentEditor.label')}</TableHead>
              <TableHead>{t('layouts.characteristics')}</TableHead>
              <TableHead className="w-0 text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableTableRows ids={ids} onReorder={reorder} disabled={disabled}>
              {value.map((c, i) => {
                const Icon = TYPE_ICONS[c.type as LayoutComponentType] ?? Type
                return (
                  <SortableTableRow key={ids[i]} id={ids[i]} handleLabel={t('layouts.dragToReorder')}>
                    <TableCell>
                      <Badge variant="outline">
                        <Icon className="size-3.5" />
                        {t(`layouts.componentTypes.${c.type}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{c.tag}</TableCell>
                    <TableCell>{c.name || <span className="text-(--ad-text-faint)">—</span>}</TableCell>
                    <TableCell className="text-(--ad-text-muted) whitespace-normal">{summary(c).join(', ')}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-0.5">
                        <RowAction label={t('common.edit')} disabled={disabled} onClick={() => setEditing({ index: i, component: c })}>
                          <Pencil className="size-4" />
                        </RowAction>
                        <RowAction label={t('common.remove')} disabled={disabled} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                          <Trash2 className="size-4" />
                        </RowAction>
                      </div>
                    </TableCell>
                  </SortableTableRow>
                )
              })}
            </SortableTableRows>
          </TableBody>
        </Table>
      )}

      <LayoutComponentDialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        component={editing?.component ?? null}
        isNew={editing?.index === -1}
        takenTags={value.filter((_, i) => i !== editing?.index).map((c) => c.tag)}
        onSave={save}
      />
    </div>
  )
}

/** Type and tag together — a tag is unique, but only checked in this editor. */
function rowId(c: LayoutComponent): string {
  return `${c.type}:${c.tag}`
}

function RowAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={label} disabled={disabled} onClick={onClick}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
